import {
  Component,
  computed,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  signal,
  Signal,
  WritableSignal
} from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';
import { UiDividerComponent } from '@app/shared/ui/divider/ui-divider.component';

import { DeviceService } from '@app/shared/services/shared/device.service';
import { TimeReportPanelFiltersService } from '@app/shared/services/time-report-panel-filters.service';
import { TimeReportFilterName } from '@app/shared/types/models/reports/time-report-panel.model';
import { DropdownItem } from '@app/shared/types/models/shared/dropdown-item';
import { UiInputComponent } from '@app/shared/ui/input/ui-input.component';
import { ProffeoUiInputValue } from '@app/shared/ui/types/input.types';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'proffeo-time-report-filters-expand',
  imports: [
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatTooltip,
    TranslatePipe,
    UiButtonComponent,
    UiButtonComponent,
    UiDividerComponent,
    UiInputComponent
  ],
  templateUrl: './time-report-filters-expand.component.html',
  styleUrls: ['./time-report-filters-expand.component.scss']
})
export class TimeReportFiltersExpandComponent {
  private readonly deviceService: DeviceService = inject(DeviceService);
  private readonly filtersService: TimeReportPanelFiltersService = inject(TimeReportPanelFiltersService);
  private readonly openMenus = signal<Record<string, boolean>>({});
  private readonly searchTermSignals = new Map<string, WritableSignal<string>>();
  private readonly filterStateSignals = new Map<
    string,
    Signal<{
      filteredItems: DropdownItem<string>[];
      allSelected: boolean;
      someSelected: boolean;
      selectedSet: Set<string>;
    }>
  >();
  private readonly displayValueSignals = new Map<string, Signal<string>>();

  protected readonly isMobile: Signal<boolean> = this.deviceService.isMobile;
  protected readonly filterConfigs = this.filtersService.filterConfigs;

  public expanded: InputSignal<boolean> = input(false);
  public filtersSearch: OutputEmitterRef<void> = output();
  public collapse: OutputEmitterRef<void> = output();

  private getSearchTermSignal(filterName: string): WritableSignal<string> {
    if (!this.searchTermSignals.has(filterName)) {
      this.searchTermSignals.set(filterName, signal(''));
    }
    return this.searchTermSignals.get(filterName)!;
  }

  protected getSearchTerm(filterName: string): string {
    return this.getSearchTermSignal(filterName)();
  }

  protected getFilterState(filterName: string): {
    filteredItems: DropdownItem<string>[];
    allSelected: boolean;
    someSelected: boolean;
    selectedSet: Set<string>;
  } {
    if (!this.filterStateSignals.has(filterName)) {
      this.filterStateSignals.set(
        filterName,
        computed(() => {
          const config = this.filterConfigs().find(c => c.name === filterName);
          if (!config) {
            return { filteredItems: [], allSelected: false, someSelected: false, selectedSet: new Set<string>() };
          }
          const selected = this.filtersService.getSelectedValues(filterName as TimeReportFilterName);
          const selectedSet = new Set(selected);
          const term = this.getSearchTermSignal(filterName)().toLowerCase();
          const filteredItems = term
            ? config.items.filter(item => item.label.toLowerCase().includes(term))
            : config.items;
          const selectedInFiltered = filteredItems.filter(item => selectedSet.has(item.value)).length;
          return {
            filteredItems,
            allSelected: filteredItems.length > 0 && selectedInFiltered === filteredItems.length,
            someSelected: selectedInFiltered > 0 && selectedInFiltered < filteredItems.length,
            selectedSet
          };
        })
      );
    }
    return this.filterStateSignals.get(filterName)!();
  }

  protected getDisplayValue(filterName: string): string {
    if (!this.displayValueSignals.has(filterName)) {
      this.displayValueSignals.set(
        filterName,
        computed(() => {
          const config = this.filterConfigs().find(c => c.name === filterName);
          if (!config) return '';
          const selected = this.filtersService.getSelectedValues(filterName as TimeReportFilterName);
          if (!selected.length) return '';
          return selected
            .map(val => config.items.find(item => item.value === val)?.label ?? '')
            .filter(Boolean)
            .join(', ');
        })
      );
    }
    return this.displayValueSignals.get(filterName)!();
  }

  protected isMenuOpen(filterName: string): boolean {
    return this.openMenus()[filterName] ?? false;
  }

  protected onMenuOpened(filterName: string): void {
    this.openMenus.update(menus => ({ ...menus, [filterName]: true }));
  }

  protected onMenuClosed(filterName: string): void {
    this.openMenus.update(menus => ({ ...menus, [filterName]: false }));
    this.getSearchTermSignal(filterName).set('');
  }

  protected setSearch(filterName: string, value: ProffeoUiInputValue): void {
    this.getSearchTermSignal(filterName).set(value === null ? '' : String(value));
  }

  protected toggleOption(filterName: string, value: string): void {
    const config = this.filterConfigs().find(item => item.name === filterName);
    if (config && !config.multiple) {
      this.filtersService.updateFilter(filterName as TimeReportFilterName, [value]);
      return;
    }

    const selected = this.filtersService.getSelectedValues(filterName as TimeReportFilterName);
    const updated = selected.includes(value) ? selected.filter(item => item !== value) : [...selected, value];
    this.filtersService.updateFilter(filterName as TimeReportFilterName, updated);
  }

  protected toggleSelectAll(filterName: string): void {
    const config = this.filterConfigs().find(item => item.name === filterName);
    if (config && !config.multiple) {
      return;
    }

    const state = this.getFilterState(filterName);
    const selected = this.filtersService.getSelectedValues(filterName as TimeReportFilterName);
    const filteredValues = state.filteredItems.map(item => item.value);
    const newValue = state.allSelected
      ? selected.filter(item => !new Set(filteredValues).has(item))
      : [...new Set([...selected, ...filteredValues])];
    this.filtersService.updateFilter(filterName as TimeReportFilterName, newValue);
  }

  protected onSearchClick(): void {
    this.filtersService.applyFilters();
    this.filtersSearch.emit();
  }

  protected onClose(): void {
    this.collapse.emit();
  }

  protected clearFilters(): void {
    this.filtersService.resetFilters();
    this.filtersSearch.emit();
  }
}
